import { tKey, type I18nKey } from "@/core/i18n";
import { Button } from "@/modules/shadcn/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shadcn/ui/dialog";
import { Label } from "@/modules/shadcn/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shadcn/ui/select";
import {
  CONVERSION_TARGETS,
  INT8_QUANTIZATION_MODES,
  isConversionTargetSupported,
  ModelBackendEnum,
  ModelOutputTypeEnum,
  ModelQuantizationEnum,
  ProjectTypeEnum,
  Rvc4QuantizationModeEnum,
  defaultQuantizationMode,
  type ConversionTarget,
} from "@repo/schema";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface AddConversionDialogProps {
  backend: ModelBackendEnum;
  trainingType: ProjectTypeEnum;
  /** Disables INT8 when the model has no calibration pack to quantize against. */
  canQuantize: boolean;
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: (input: {
    target: ConversionTarget;
    precision: ModelQuantizationEnum;
    quantizationMode?: Rvc4QuantizationModeEnum;
  }) => void;
}

const MODE_LABELS = {
  [Rvc4QuantizationModeEnum.INT8_STANDARD]: tKey("model:addConversion.modes.INT8_STANDARD"),
  [Rvc4QuantizationModeEnum.INT8_ACCURACY_FOCUSED]: tKey(
    "model:addConversion.modes.INT8_ACCURACY_FOCUSED",
  ),
  [Rvc4QuantizationModeEnum.INT8_INT16_MIXED]: tKey(
    "model:addConversion.modes.INT8_INT16_MIXED",
  ),
  [Rvc4QuantizationModeEnum.INT8_INT16_MIXED_ACCURACY_FOCUSED]: tKey(
    "model:addConversion.modes.INT8_INT16_MIXED_ACCURACY_FOCUSED",
  ),
} satisfies Record<(typeof INT8_QUANTIZATION_MODES)[number], I18nKey<"model">>;

export const AddConversionDialog = ({
  backend,
  trainingType,
  canQuantize,
  isLoading,
  onCancel,
  onConfirm,
}: AddConversionDialogProps) => {
  const { t } = useTranslation(["model", "common"]);
  const [target, setTarget] = useState<ConversionTarget>(
    ModelOutputTypeEnum.RVC4,
  );
  const [precision, setPrecision] = useState<ModelQuantizationEnum>(
    ModelQuantizationEnum.FP16,
  );
  const [mode, setMode] = useState<Rvc4QuantizationModeEnum>(
    Rvc4QuantizationModeEnum.INT8_INT16_MIXED,
  );

  // Legacy RF-DETR models, and Ultra Vision segmentation, have no RVC2/RVC3
  // conversion path — their graphs only lower on RVC4.
  const isRfdetr = backend === ModelBackendEnum.RF_DETR;
  const availableTargets: ConversionTarget[] = CONVERSION_TARGETS.filter(
    (value) =>
      (!isRfdetr || value === ModelOutputTypeEnum.RVC4) &&
      isConversionTargetSupported(backend, trainingType, value),
  );
  const restrictedToRvc4 = availableTargets.length < CONVERSION_TARGETS.length;

  const isInt8 = precision === ModelQuantizationEnum.INT8;
  // quantization_mode is an RVC4-only field; RVC2/RVC3 express precision
  // through compress_to_fp16 and disable_calibration instead.
  const showMode = target === ModelOutputTypeEnum.RVC4 && isInt8;

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("outputs.addConversion")}</DialogTitle>
          <DialogDescription>{t("addConversion.description")}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label>{t("addConversion.targetPlatform")}</Label>
            <Select
              value={target}
              onValueChange={(v) => setTarget(v as ConversionTarget)}
            >
              <SelectTrigger className="h-8!">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableTargets.map((value) => (
                  <SelectItem key={value} value={value}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {restrictedToRvc4 && (
              <span className="text-muted-foreground text-xs">
                {isRfdetr
                  ? t("addConversion.rfdetrRvc4Only")
                  : t("addConversion.rvc4Only")}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label>{t("addConversion.precision")}</Label>
            <Select
              value={precision}
              onValueChange={(v) => setPrecision(v as ModelQuantizationEnum)}
            >
              <SelectTrigger className="h-8!">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ModelQuantizationEnum.FP16}>
                  {t("addConversion.fp16")}
                </SelectItem>
                <SelectItem
                  value={ModelQuantizationEnum.INT8}
                  disabled={!canQuantize}
                >
                  {t("addConversion.int8")}
                </SelectItem>
              </SelectContent>
            </Select>
            {!canQuantize && (
              <span className="text-muted-foreground text-xs">
                {t("addConversion.int8NeedsCalibration")}
              </span>
            )}
          </div>

          {showMode && (
            <div className="flex flex-col gap-2">
              <Label>{t("addConversion.quantizationMode")}</Label>
              <Select
                value={mode}
                onValueChange={(v) => setMode(v as Rvc4QuantizationModeEnum)}
              >
                <SelectTrigger className="h-8!">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INT8_QUANTIZATION_MODES.map((m) => (
                    <SelectItem key={m} value={m}>
                      {t(MODE_LABELS[m])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onCancel}>
            {t("common:actions.cancel")}
          </Button>
          <Button
            size="sm"
            disabled={isLoading}
            onClick={() =>
              onConfirm({
                target,
                precision,
                quantizationMode:
                  target === ModelOutputTypeEnum.RVC4
                    ? isInt8
                      ? mode
                      : defaultQuantizationMode(precision)
                    : undefined,
              })
            }
          >
            {isLoading ? t("addConversion.starting") : t("addConversion.start")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
